import React, { useState, useEffect, Fragment } from "react";
import {
    AppBar,
    Toolbar,
    Typography,
    Tabs,
    Tab,
    Box,
    Menu,
    MenuItem,
    IconButton,
    Drawer,
    List,
    ListItem,
    ListItemText,
    Divider,
    useMediaQuery,
    useTheme,
    Avatar,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import Panel from "./Panel";
import Report from "../Report";
import Qr from "./Qr/index";
import Staff from "./Staff/index";
import AdManagement from "./AdManagement";
import Profile from "./Profile";
import ServiceManagement from "./ServiceManagement";
import MenuManagement from "./MenuManagement";
import TextEditor from "./TextEditor";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import HelpIcon from "@mui/icons-material/Help";
import Logo from "../../assests/logo.png";
import { getCurrentBranchId, getCurrentUser } from "../../utils/auth";
import { planCan } from "../../utils/plan";
import { getTutorialProgress, saveTutorialProgress } from "../../api/api";
import { getFrontendUrl, getStorageUrl } from "../../config/config";
import TutorialOverlay from "../../components/TutorialOverlay";
import OnboardingTutorialOverlay from "../../components/OnboardingTutorialOverlay";

const AdminHome = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const branchId = getCurrentBranchId();
    const currentUser = getCurrentUser();
    
    // Check if user is a free trial user (backend may send boolean, number, or string)
    const isFreeTrialUser =
        currentUser?.is_free_trial === true ||
        currentUser?.is_free_trial === 1 ||
        currentUser?.is_free_trial === "1" ||
        currentUser?.is_free_trial === "true";

    // Separate dropdown menu state for Display Options, Queue NR, and Settings
    const [anchorElDisplay, setAnchorElDisplay] = useState(null);
    const [anchorElQueue, setAnchorElQueue] = useState(null);
    const [anchorElSettings, setAnchorElSettings] = useState(null);
    
    // Tutorial modal state
    const [tutorialOpen, setTutorialOpen] = useState(false);

    // Onboarding tutorial state (first-time free trial)
    const [runOnboardingTutorial, setRunOnboardingTutorial] = useState(false);
    const [onboardingPage, setOnboardingPage] = useState(null);
    const [tutorialProgress, setTutorialProgress] = useState(null);

    const handleMenuOpenDisplay = (event) => {
        console.log("Opening Display Options dropdown");
        setAnchorElDisplay(event.currentTarget);
    };

    const handleMenuOpenQueue = (event) => {
        console.log("Opening Queue NR dropdown");
        setAnchorElQueue(event.currentTarget);
    };

    const handleMenuOpenSettings = (event) => {
        console.log("Opening Settings dropdown");
        setAnchorElSettings(event.currentTarget);
    };

    const handleMenuClose = () => {
        console.log("Closing dropdowns");
        setAnchorElDisplay(null);
        setAnchorElQueue(null);
        setAnchorElSettings(null);
    };

    const handleDropdownClick = (path) => {
        console.log("Display option clicked:", path);
        window.open(path, "_blank"); // Open in new tab
        handleMenuClose();
    };

    const handleDropdownClickQueueNR = (path) => {
        console.log("Queue NR option clicked:", path);
        window.open(path, "_blank"); // Open in new tab for Queue NR options
        handleMenuClose();
    };

    const handleDropdownClickSettings = (path) => {
        console.log("Settings option clicked:", path);
        navigate(path);
        handleMenuClose();
    };

    // Helper function to generate customer view URLs
    const getCustomerViewUrl = (ticketCode = "DEMO") => {
        return `/getticket/${ticketCode}`;
    };

    // Mapping tabs
    const allTabs = [
        { label: "LIVE QUEUE", path: "/home/staff", component: <Staff /> },
        { label: "STATS", path: "/home/panel", component: <Panel /> },
        {
            label: "DISPLAY OPTIONS",
            dropdown: true,
            options: [
                {
                    label: "DISPLAY",
                    path: `/home/display?branch_id=${branchId}`,
                },
                {
                    label: "DISPLAY AD",
                    path: `/home/display_ad?branch_id=${branchId}`,
                },
            ],
        },
        {
            label: "QR",
            path: "/home/qr",
            component: <Qr />,
            newTab: true,
        },
        {
            label: "SETTINGS",
            dropdown: true,
            options: [
                { label: "SERVICES", path: "/home/service-management" },
                { label: "MENU", path: "/home/menu-management" },
                // Text editor is a paid feature; hidden on plans that don't include it.
                ...(planCan("text_editor", currentUser)
                    ? [{ label: "TEXT EDITOR", path: "/home/text-editor" }]
                    : []),
            ],
        },
        {
            label: "PROFILE",
            path: "/home/profile",
            component: <Profile />,
        },
        { label: "REPORT", path: "/home/report", component: <Report /> },
        {
            label: "CUSTOMER VIEW",
            path: getCustomerViewUrl("DEMO"),
            newTab: true,
            component: <Typography>Customer View Content</Typography>,
        },
        {
            label: "AD DISPLAY",
            dropdown: true,
            options: [
                {
                    label: "DISPLAY AD LANDSCAPE",
                    path: `/home/display_ad_Landscape?branch_id=${branchId}`,
                },
                {
                    label: "DISPLAY AD PORTRAIT",
                    path: `/home/display_ad_potrait?branch_id=${branchId}`,
                },
                {
                    label: "DISPLAY AD CAROUSEL LANDSCAPE",
                    path: `/home/display_ad_Carousel_landscape?branch_id=${branchId}`,
                },
                {
                    label: "DISPLAY AD CAROUSEL PORTRAIT",
                    path: `/home/display_ad_Carousel_Portrait?branch_id=${branchId}`,
                },
            ],
        },
    ];
    
    // For free trial: hide AD DISPLAY, replace DISPLAY OPTIONS with single DISPLAY link
    const tabs = isFreeTrialUser
        ? allTabs.filter(tab => tab.label !== "AD DISPLAY").map(tab =>
            tab.label === "DISPLAY OPTIONS"
                ? { label: "DISPLAY", path: `/home/display?branch_id=${branchId}`, newTab: true }
                : tab
        )
        : allTabs;

    // Helper function to extract pathname from a path (remove query parameters)
    const getPathname = (path) => {
        if (!path) return '';
        return path.split('?')[0];
    };

    // Get active tab index from URL
    // Check both direct tab paths and dropdown option paths
    const activeTabIndex = tabs.findIndex((tab) => {
        // Check direct path
        if (tab.path && getPathname(tab.path) === location.pathname) {
            return true;
        }
        // Check dropdown options
        if (tab.dropdown && tab.options) {
            return tab.options.some(
                (option) => getPathname(option.path) === location.pathname
            );
        }
        return false;
    });

    useEffect(() => {
        // Check if user is free trial and needs to see welcome screen
        if (isFreeTrialUser && currentUser?.email) {
            // Check if welcome screen should be shown
            const welcomeShown = localStorage.getItem('welcome_screen_shown');
            if (!welcomeShown && location.pathname !== '/home/welcome') {
                navigate("/home/welcome", { replace: true });
                return;
            }
        }
        
        if (activeTabIndex === -1) {
            navigate("/home/panel", { replace: true });
        }
        
        // Check if tutorial should be shown on first visit (only for free trial users)
        // Don't show full dashboard tour if initial 4-step onboarding is pending or in progress
        const tutorialShown = localStorage.getItem('tutorial_shown');
        const onboardingPending = localStorage.getItem('startOnboardingTour') === 'true';
        const initialOnboardingInProgress = localStorage.getItem('initialOnboardingInProgress') === 'true';
        if (isFreeTrialUser && !tutorialShown && !onboardingPending && !initialOnboardingInProgress && !runOnboardingTutorial && location.pathname === '/home/panel' && location.pathname !== '/home/welcome') {
            // Show full dashboard tutorial after a short delay on first visit (not during 4-step intro)
            const timer = setTimeout(() => {
                setTutorialOpen(true);
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, [activeTabIndex, navigate, location.pathname, isFreeTrialUser, runOnboardingTutorial]);

    // Fetch onboarding tutorial progress for free trial users
    useEffect(() => {
        if (!isFreeTrialUser) return;
        getTutorialProgress(branchId)
            .then((res) => {
                if (res?.success && res?.progress) setTutorialProgress(res.progress);
            })
            .catch(() => {});
    }, [branchId, isFreeTrialUser]);

    // Trigger initial 4-step onboarding from localStorage flag (set during registration)
    // Only runs the 4 steps (dashboard → qr → live_queue → dashboard_final), not the full dashboard menu tour
    // Note: startOnboardingTour is ONLY set from free trial registration, so we trigger regardless of isFreeTrialUser
    // Flag removal is deferred to the timer callback so React StrictMode's cleanup+re-run cycle
    // doesn't eat the flag before the tour can start.
    useEffect(() => {
        if (location.pathname !== "/home/panel") return;
        const startFlag = localStorage.getItem("startOnboardingTour");
        if (startFlag === "true") {
            localStorage.setItem("initialOnboardingInProgress", "true");
            // Small delay to let the page render before starting the tour
            const timer = setTimeout(() => {
                localStorage.removeItem("startOnboardingTour");
                setOnboardingPage("initial_tour");
                setRunOnboardingTutorial(true);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [location.pathname]);

    // Trigger dashboard_final onboarding from navigation state (returning from live_queue)
    useEffect(() => {
        if (!isFreeTrialUser || location.pathname !== "/home/panel") return;
        const navStartFlag = location.state?.startOnboardingTour;
        if (navStartFlag === "dashboard_final") {
            const timer = setTimeout(() => {
                setOnboardingPage("dashboard_final");
                setRunOnboardingTutorial(true);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [isFreeTrialUser, location.pathname, location.state?.startOnboardingTour]);

    // Auto-detect: if live_queue is done but dashboard_final is not
    useEffect(() => {
        if (!isFreeTrialUser || location.pathname !== "/home/panel") return;
        if (!tutorialProgress) return;
        const phases = tutorialProgress?.onboardingPhases || {};
        if (phases.live_queue === true && phases.dashboard_final !== true) {
            const timer = setTimeout(() => {
                setOnboardingPage("dashboard_final");
                setRunOnboardingTutorial(true);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [isFreeTrialUser, location.pathname, tutorialProgress]);

    const handleOnboardingPhaseComplete = async (page) => {
        setRunOnboardingTutorial(false);
        localStorage.removeItem("initialOnboardingInProgress");
        try {
            const res = await getTutorialProgress(branchId);
            const progress = res?.success && res?.progress ? { ...res.progress } : {};
            progress.onboardingPhases = progress.onboardingPhases || {};
            progress.onboardingPhases[page] = true;
            // When the combined initial tour completes, mark all legacy phases done
            // so QR/Staff page overlays don't re-trigger
            if (page === "initial_tour") {
                progress.onboardingPhases.dashboard = true;
                progress.onboardingPhases.qr = true;
                progress.onboardingPhases.live_queue = true;
                progress.onboardingPhases.dashboard_final = true;
            }
            await saveTutorialProgress(branchId, progress);
            setTutorialProgress(progress);
        } catch (e) {
            console.error("Failed to save onboarding phase:", e);
        }
    };

    const handleTabChange = (event, newValue) => {
        if (tabs[newValue].newTab) {
            window.open(tabs[newValue].path, "_blank"); // Open in new tab
        } else if (!tabs[newValue].dropdown) {
            navigate(tabs[newValue].path);
        }
    };

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const [drawerOpen, setDrawerOpen] = useState(false);

    const toggleDrawer = (open) => () => {
        setDrawerOpen(open);
    };
    const handleLogout = () => {
        localStorage.removeItem("token"); // Remove the stored token
        window.location.href = "/login"; // Redirect to login or homepage
    };

    // Profile-specific tutorial state
    const [profileTutorialOpen, setProfileTutorialOpen] = useState(false);

    const handleTutorialOpen = () => {
        if (location.pathname === "/home/profile") {
            setProfileTutorialOpen(true);
        } else {
            setTutorialOpen(true);
        }
    };

    const handleTutorialClose = () => {
        setTutorialOpen(false);
    };

    const handleTutorialComplete = () => {
        localStorage.setItem('tutorial_shown', 'true');
    };

    const handleSignupTourOpen = () => {
        setOnboardingPage("initial_tour");
        setRunOnboardingTutorial(true);
    };

    const isQrPage = location.pathname === "/home/qr";

    return (
        <Box>
            {!isQrPage && <AppBar position="static">
                <Toolbar sx={{ justifyContent: "space-between", overflow: "hidden" }}>
                    {isMobile ? (
                        <>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                <img
                                    src={Logo}
                                    alt="Logo"
                                    style={{
                                        height: "32px",
                                        marginRight: "16px",
                                        filter: "brightness(0) invert(1)", // Make logo white
                                    }}
                                />
                            </Box>
                            <IconButton
                                edge="end"
                                color="inherit"
                                onClick={toggleDrawer(true)}
                            >
                                <MenuIcon />
                            </IconButton>
                            <Drawer
                                anchor="right"
                                open={drawerOpen}
                                onClose={toggleDrawer(false)}
                            >
                                <Box
                                    sx={{ width: 250 }}
                                    role="presentation"
                                    onClick={toggleDrawer(false)}
                                    onKeyDown={toggleDrawer(false)}
                                >
                                    <List>
                                        {tabs.map((tab, index) => {
                                            if (tab.newTab) {
                                                return (
                                                    <ListItem
                                                        button
                                                        key={index}
                                                        onClick={(e) =>
                                                            handleTabChange(
                                                                e,
                                                                index
                                                            )
                                                        }
                                                    >
                                                        <ListItemText
                                                            primary={tab.label}
                                                        />
                                                    </ListItem>
                                                );
                                            } else if (tab.dropdown) {
                                                return (
                                                    <Fragment key={index}>
                                                        <ListItem>
                                                            <ListItemText
                                                                primary={
                                                                    tab.label
                                                                }
                                                                sx={{
                                                                    fontWeight:
                                                                        "bold",
                                                                    color: "primary.main",
                                                                }}
                                                            />
                                                        </ListItem>
                                                        {tab.options.map(
                                                            (option, i) => (
                                                                <ListItem
                                                                    button
                                                                    key={`${tab.label}-${i}`}
                                                                    sx={{
                                                                        pl: 4,
                                                                    }}
                                                                    onClick={() => {
                                                                        if (tab.label === "DISPLAY OPTIONS") {
                                                                            handleDropdownClick(option.path);
                                                                        } else if (tab.label === "AD DISPLAY") {
                                                                            handleDropdownClickQueueNR(option.path);
                                                                        } else if (tab.label === "SETTINGS") {
                                                                            handleDropdownClickSettings(option.path);
                                                                        }
                                                                    }}
                                                                >
                                                                    <ListItemText
                                                                        primary={
                                                                            option.label
                                                                        }
                                                                    />
                                                                </ListItem>
                                                            )
                                                        )}
                                                    </Fragment>
                                                );
                                            } else {
                                                return (
                                                    <ListItem
                                                        button
                                                        key={index}
                                                        onClick={(e) =>
                                                            handleTabChange(
                                                                e,
                                                                index
                                                            )
                                                        }
                                                    >
                                                        <ListItemText
                                                            primary={tab.label}
                                                        />
                                                    </ListItem>
                                                );
                                            }
                                        })}
                                        <Divider sx={{ my: 1 }} />
                                        <ListItem
                                            button
                                            onClick={() => {
                                                toggleDrawer(false)();
                                                handleTutorialOpen();
                                            }}
                                            sx={{
                                                "&:hover": {
                                                    backgroundColor: "primary.light",
                                                    color: "white",
                                                },
                                            }}
                                        >
                                            <HelpIcon sx={{ mr: 2 }} />
                                            <ListItemText primary="Tutorial" />
                                        </ListItem>
                                        <ListItem
                                            button
                                            onClick={() => {
                                                toggleDrawer(false)();
                                                handleSignupTourOpen();
                                            }}
                                            sx={{
                                                "&:hover": {
                                                    backgroundColor: "primary.light",
                                                    color: "white",
                                                },
                                            }}
                                        >
                                            <HelpIcon sx={{ mr: 2 }} />
                                            <ListItemText primary="Signup tour" />
                                        </ListItem>
                                        <ListItem
                                            button
                                            onClick={() => {
                                                toggleDrawer(false)();
                                                navigate("/home/profile");
                                            }}
                                            sx={{
                                                "&:hover": {
                                                    backgroundColor: "primary.light",
                                                    color: "white",
                                                },
                                            }}
                                        >
                                            {currentUser?.logo ? (
                                                <Avatar
                                                    src={getStorageUrl(`/storage/logos/${currentUser.logo}`)}
                                                    alt="Profile"
                                                    sx={{
                                                        width: 24,
                                                        height: 24,
                                                        mr: 2,
                                                    }}
                                                />
                                            ) : (
                                                <AccountCircleIcon sx={{ mr: 2 }} />
                                            )}
                                            <ListItemText primary="Profile" />
                                        </ListItem>
                                        <ListItem
                                            button
                                            onClick={() => {
                                                toggleDrawer(false)();
                                                handleLogout();
                                            }}
                                            sx={{
                                                color: "error.main",
                                                "&:hover": {
                                                    backgroundColor: "error.light",
                                                    color: "white",
                                                },
                                            }}
                                        >
                                            <LogoutIcon sx={{ mr: 2 }} />
                                            <ListItemText primary="Logout" />
                                        </ListItem>
                                    </List>
                                </Box>
                            </Drawer>
                        </>
                    ) : (
                        <Box sx={{ display: "flex", alignItems: "center", flex: "1 1 auto", minWidth: 0 }}>
                            <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
                                <img
                                    src={Logo}
                                    alt="Logo"
                                    style={{
                                        height: "32px",
                                        marginRight: "16px",
                                        filter: "brightness(0) invert(1)", // Make logo white
                                    }}
                                />
                            </Box>
                            <Box
                                data-tutorial-id="tutorial-nav-tabs"
                                sx={{ flexGrow: 0, flexShrink: 0, ml: 3, display: "flex", alignItems: "center" }}
                            >
                                <Tabs
                                    value={
                                        activeTabIndex !== -1 ? activeTabIndex : 0
                                    }
                                    onChange={handleTabChange}
                                    textColor="inherit"
                                    indicatorColor="secondary"
                                    variant="scrollable"
                                    scrollButtons="auto"
                                    sx={{ flexGrow: 0, minWidth: 0 }}
                                >
                                    {tabs.map((tab, index) =>
                                        tab.newTab ? (
                                            <Tab
                                                key={index}
                                                label={tab.label}
                                                {...(tab.label === "QR" ? { "data-onboarding-id": "onboarding-nav-qr" } : {})}
                                            />
                                        ) : tab.dropdown ? (
                                            <Tab
                                                key={index}
                                                {...(tab.label === "SETTINGS" ? { "data-tutorial-id": "tutorial-settings" } : {})}
                                                label={
                                                    <Box
                                                        display="flex"
                                                        alignItems="center"
                                                    >
                                                        {tab.label}
                                                        <ArrowDropDownIcon
                                                            sx={{
                                                                ml: 0.5,
                                                                transform:
                                                                    (tab.label ===
                                                                        "DISPLAY OPTIONS" &&
                                                                        Boolean(
                                                                            anchorElDisplay
                                                                        )) ||
                                                                    (tab.label ===
                                                                        "AD DISPLAY" &&
                                                                        Boolean(
                                                                            anchorElQueue
                                                                        )) ||
                                                                    (tab.label ===
                                                                        "SETTINGS" &&
                                                                        Boolean(
                                                                            anchorElSettings
                                                                        ))
                                                                        ? "rotate(180deg)"
                                                                        : "rotate(0deg)",
                                                                transition:
                                                                    "transform 0.2s ease-in-out",
                                                            }}
                                                        />
                                                    </Box>
                                                }
                                                onClick={
                                                    tab.label === "DISPLAY OPTIONS"
                                                        ? handleMenuOpenDisplay
                                                        : tab.label === "AD DISPLAY"
                                                        ? handleMenuOpenQueue
                                                        : tab.label === "SETTINGS"
                                                        ? handleMenuOpenSettings
                                                        : null
                                                }
                                                sx={{
                                                    "&.Mui-selected": {
                                                        backgroundColor:
                                                            (tab.label ===
                                                                "DISPLAY OPTIONS" &&
                                                                Boolean(
                                                                    anchorElDisplay
                                                                )) ||
                                                            (tab.label ===
                                                                "AD DISPLAY" &&
                                                                Boolean(
                                                                    anchorElQueue
                                                                )) ||
                                                            (tab.label ===
                                                                "SETTINGS" &&
                                                                Boolean(
                                                                    anchorElSettings
                                                                ))
                                                                ? "rgba(255, 255, 255, 0.1)"
                                                                : "transparent",
                                                    },
                                                }}
                                            />
                                        ) : (
                                            <Tab
                                                key={index}
                                                label={tab.label}
                                                {...(tab.label === "STATS" ? { "data-tutorial-id": "tutorial-stats" } : tab.label === "LIVE QUEUE" ? { "data-tutorial-id": "tutorial-live-que" } : {})}
                                            />
                                        )
                                    )}
                                </Tabs>
                                <IconButton
                                    color="inherit"
                                    onClick={handleTutorialOpen}
                                    data-tutorial-id="tutorial-help"
                                    sx={{
                                        flexShrink: 0,
                                        ml: 0.5,
                                        mr: 0,
                                        "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
                                    }}
                                    title="Show Tutorial"
                                >
                                    <HelpIcon />
                                </IconButton>
                            </Box>
                            {/* Spacer: fills gap so Profile/Logout stay on the far right */}
                            <Box sx={{ flexGrow: 1, minWidth: 24 }} />
                        </Box>
                    )}

                    {!isMobile && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <IconButton
                                color="inherit"
                                onClick={handleSignupTourOpen}
                                sx={{
                                    "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
                                }}
                                title="Replay signup tour"
                                aria-label="Replay signup tour"
                            >
                                <HelpIcon />
                            </IconButton>
                            <IconButton
                                color="inherit"
                                onClick={() => navigate("/home/profile")}
                                sx={{
                                    "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
                                    p: 0.5,
                                }}
                                title="Profile"
                            >
                                {currentUser?.logo ? (
                                    <Avatar
                                        src={getStorageUrl(`/storage/logos/${currentUser.logo}`)}
                                        alt="Profile"
                                        sx={{
                                            width: 32,
                                            height: 32,
                                            border: "1px solid rgba(255, 255, 255, 0.3)",
                                        }}
                                    />
                                ) : (
                                    <AccountCircleIcon />
                                )}
                            </IconButton>
                            <Typography
                                sx={{
                                    color: "#FFFF",
                                    fontWeight: "bold",
                                    cursor: "pointer",
                                    flexShrink: 0,
                                    whiteSpace: "nowrap",
                                    "&:hover": { textDecoration: "underline" },
                                }}
                                onClick={handleLogout}
                            >
                                Logout
                            </Typography>
                        </Box>
                    )}
                </Toolbar>
            </AppBar>}

            {/* Dropdown Menus (desktop only) */}
            <Menu
                anchorEl={anchorElDisplay}
                open={Boolean(anchorElDisplay)}
                onClose={handleMenuClose}
                PaperProps={{
                    sx: {
                        mt: 1,
                        minWidth: 200,
                        boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                    },
                }}
            >
                {tabs
                    .find((tab) => tab.label === "DISPLAY OPTIONS")
                    ?.options.map((option, index) => (
                        <MenuItem
                            key={index}
                            onClick={() => handleDropdownClick(option.path)}
                            sx={{
                                "&:hover": {
                                    backgroundColor: "primary.light",
                                    color: "white",
                                },
                            }}
                        >
                            {option.label}
                        </MenuItem>
                    ))}
            </Menu>

            {/* Dropdown Menu for "AD DISPLAY" */}
            <Menu
                anchorEl={anchorElQueue}
                open={Boolean(anchorElQueue)}
                onClose={handleMenuClose}
                PaperProps={{
                    sx: {
                        mt: 1,
                        minWidth: 200,
                        boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                    },
                }}
            >
                {tabs
                    .find((tab) => tab.label === "AD DISPLAY")
                    ?.options.map((option, index) => (
                        <MenuItem
                            key={index}
                            onClick={() =>
                                handleDropdownClickQueueNR(option.path)
                            }
                            sx={{
                                "&:hover": {
                                    backgroundColor: "primary.light",
                                    color: "white",
                                },
                            }}
                        >
                            {option.label}
                        </MenuItem>
                    ))}
            </Menu>

            {/* Dropdown Menu for "SETTINGS" */}
            <Menu
                anchorEl={anchorElSettings}
                open={Boolean(anchorElSettings)}
                onClose={handleMenuClose}
                PaperProps={{
                    sx: {
                        mt: 1,
                        minWidth: 200,
                        boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                    },
                }}
            >
                {tabs
                    .find((tab) => tab.label === "SETTINGS")
                    ?.options.map((option, index) => (
                        <MenuItem
                            key={index}
                            onClick={() =>
                                handleDropdownClickSettings(option.path)
                            }
                            sx={{
                                "&:hover": {
                                    backgroundColor: "primary.light",
                                    color: "white",
                                },
                            }}
                        >
                            {option.label}
                        </MenuItem>
                    ))}
            </Menu>

            {/* Content Area */}
            <Box
                data-tutorial-id="tutorial-content"
                sx={{ p: 3, height: isQrPage ? "100vh" : "calc(100vh - 64px)", overflow: "auto" }}
            >
                {location.pathname === "/home/service-management"
                    ? <ServiceManagement />
                    : location.pathname === "/home/menu-management"
                    ? <MenuManagement />
                    : location.pathname === "/home/text-editor"
                    ? <TextEditor />
                    : tabs[activeTabIndex]?.component || <Panel />}
            </Box>

            {/* Interactive dashboard tutorial (driver.js) – always uses default dashboard steps.
                Page-specific onboarding tours are handled by each page's own OnboardingTutorialOverlay. */}
            <TutorialOverlay
                run={tutorialOpen}
                onClose={handleTutorialClose}
                onComplete={handleTutorialComplete}
            />
            <OnboardingTutorialOverlay
                page={onboardingPage}
                run={runOnboardingTutorial}
                onPhaseComplete={handleOnboardingPhaseComplete}
                onClose={() => {
                    setRunOnboardingTutorial(false);
                    // Don't remove initialOnboardingInProgress here — it guards
                    // against the full navbar tutorial firing during the multi-page
                    // onboarding flow.  Only handleOnboardingPhaseComplete("dashboard_final")
                    // should remove it once the entire onboarding is done.
                }}
            />
            <OnboardingTutorialOverlay
                page="profile"
                run={profileTutorialOpen}
                onPhaseComplete={() => setProfileTutorialOpen(false)}
                onClose={() => setProfileTutorialOpen(false)}
            />
        </Box>
    );
};

export default AdminHome;
